<script setup lang="ts">
import { computed, shallowRef } from 'vue';
import {
  UptimeCalendarBoundary,
  UptimeCalendarRange,
  UptimeOutage,
  UptimeService,
} from '@/utils/api/uptime';
import {
  getOutageDuration,
  getUptimeStatusColor,
  getUptimeStatusText,
} from '@/modules/uptime/helpers/uptime';
import {
  eachDayOfInterval,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  parse as parseDate,
  startOfMonth,
} from 'date-fns';
import { formatAnyDate } from '@/utils/helpers/dates';
import { VCalendar as Calendar } from 'vuetify/lib';

interface CalendarEvent {
  name: string;
  start: Date;
  end: Date;
  color: string;
  timed: boolean;
}

const props = defineProps<{
  service: UptimeService;
}>();

const emit = defineEmits<{
  (event: 'close-modal'): void;
}>();

const rCalendar = shallowRef<
  null | (InstanceType<typeof Calendar> & { prev: () => void; next: () => void; title: string })
>(null);

const currDay = shallowRef('');
const currMonth = shallowRef(startOfMonth(new Date()));
const serviceOutages = shallowRef<UptimeOutage[]>([]);

const isFirstMonth = computed(() => isSameMonth(currMonth.value, props.service.rangeStart));

const isLastMonth = computed(() => isSameMonth(currMonth.value, props.service.rangeEnd));

const isCurrentMonth = computed(() => isSameMonth(currMonth.value, new Date()));

function getCalendarEvents(day: string): CalendarEvent[] {
  const firstDay = parseDate(day, 'yyyy-MM-dd', new Date());
  return serviceOutages.value
    .filter(
      (o) => o.from.getDate() === firstDay.getDate() && o.from.getMonth() === firstDay.getMonth()
    )
    .map((o) => {
      return {
        name: getUptimeStatusText(o.status),
        start: o.from,
        end: o.until,
        color: getUptimeStatusColor(o.status),
        timed: false,
      };
    });
}

function eachDayOfService(): Date[] {
  return eachDayOfInterval({
    start: props.service.rangeStart,
    end: props.service.rangeEnd,
  }).reverse();
}

function eachOutageInDay(dt: Date): UptimeOutage[] {
  return serviceOutages.value.filter((o) => isSameDay(o.from, dt) || isSameDay(o.until, dt));
}

function getDayTitle(dt: Date): string {
  return formatAnyDate(dt, 'MMMM d yyyy');
}

function getOutageTime(reportDate: Date, outageDate: Date): string {
  let timeFormat = 'HH:mm';
  if (!isSameDay(reportDate, outageDate)) {
    timeFormat = 'HH:mm (MMM dd)';
  }
  return formatAnyDate(outageDate, timeFormat);
}

function filterMonthOutages({ start: sof, end: eom }: UptimeCalendarRange): void {
  const firstDay = parseDate(sof.date as string, 'yyyy-MM-dd', new Date());
  const lastDay = parseDate(eom.date as string, 'yyyy-MM-dd', new Date());
  currMonth.value = firstDay;
  serviceOutages.value = props.service.outages.filter(
    (o) => !(isAfter(o.from, lastDay) || isBefore(o.until, firstDay))
  );
}

function viewDay({ date }: UptimeCalendarBoundary): void {
  currDay.value = date;
}

function getEventColor(event: CalendarEvent): string {
  return event.color;
}

function setToday(): void {
  currDay.value = '';
}

function prevMonth(): void {
  rCalendar.value?.prev();
}

function nextMonth(): void {
  rCalendar.value?.next();
}

function closeDialog(): void {
  emit('close-modal');
}
</script>

<template>
  <VDialog
    v-shortkey="['esc']"
    content-class="au-popup-dialog"
    max-width="1200px"
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistents
    :value="true"
    @click:outside="closeDialog()"
    @keydown.esc="closeDialog()"
    @shortkey.native="closeDialog()"
  >
    <VCard>
      <VCardTitle>{{ service.name }}</VCardTitle>
      <VCardSubtitle>Historical Overview</VCardSubtitle>
      <VCardText>
        <VRow class="fill-height">
          <VCol>
            <VSheet height="64">
              <VToolbar flat>
                <VBtn
                  class="mr-4"
                  color="secondary"
                  :disabled="isCurrentMonth"
                  outlined
                  @click="setToday"
                >
                  Today
                </VBtn>
                <VBtn color="secondary" :disabled="isFirstMonth" fab small text @click="prevMonth">
                  <VIcon small> mdi-chevron-left</VIcon>
                </VBtn>
                <VBtn color="secondary" :disabled="isLastMonth" fab small text @click="nextMonth">
                  <VIcon small> mdi-chevron-right</VIcon>
                </VBtn>
                <VToolbarTitle v-if="rCalendar" class="pl-4">
                  {{ rCalendar.title }}
                </VToolbarTitle>
              </VToolbar>
            </VSheet>
            <VSheet height="360">
              <VCalendar
                ref="rCalendar"
                v-model="currDay"
                color="primary"
                type="month"
                @change="filterMonthOutages"
                @click:date="viewDay"
                @click:more="viewDay"
              >
                <template #day="{ past, date }">
                  <VRow justify="center">
                    <template v-if="past">
                      <VSheet
                        v-for="(calendarEvent, i) in getCalendarEvents(date)"
                        :key="i"
                        class="mt-2 mb-1"
                        :color="getEventColor(calendarEvent)"
                        height="8"
                        rounded
                        :title="calendarEvent.name"
                        :width="8"
                      ></VSheet>
                    </template>
                  </VRow>
                </template>
              </VCalendar>
            </VSheet>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>
    <VCard>
      <VCardSubtitle>Past Incidents</VCardSubtitle>
      <VCardText>
        <VContainer class="scrollable-container">
          <template v-if="serviceOutages.length === 0">
            <span><strong>No incidents this month.</strong></span>
          </template>
          <template v-for="(reportDate, i) in eachDayOfService()" v-else>
            <template v-if="eachOutageInDay(reportDate).length > 0">
              <VList :key="i" subheader two-line>
                <VSubheader>{{ getDayTitle(reportDate) }}</VSubheader>
                <VListItem v-for="(outage, j) in eachOutageInDay(reportDate)" :key="j">
                  <VTimeline align-top class="outage-report pt-3" dense>
                    <VTimelineItem class="pt-0 pb-2" color="info" small>
                      <VRow no-gutters>
                        <VCol cols="2">
                          <strong>{{ getOutageTime(reportDate, outage.until) }}</strong>
                        </VCol>
                        <VCol>
                          <strong>Resolved</strong>
                          <div>
                            The incident status has changed to resolved after
                            {{ getOutageDuration(outage) }}
                          </div>
                        </VCol>
                      </VRow>
                    </VTimelineItem>
                    <VTimelineItem :color="getUptimeStatusColor(outage.status)" small>
                      <VRow no-gutters>
                        <VCol cols="2">
                          <strong>{{ getOutageTime(reportDate, outage.from) }}</strong>
                        </VCol>
                        <VCol>
                          <strong>{{ getUptimeStatusText(outage.status) }}</strong>
                        </VCol>
                      </VRow>
                    </VTimelineItem>
                  </VTimeline>
                </VListItem>
              </VList>
            </template>
          </template>
        </VContainer>
      </VCardText>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
.scrollable-container {
  height: calc(100vh - 800px);
  overflow-y: auto;
}

.outage-report {
  width: 900px;
}
</style>
